import type { ApiBillingConfig, ApiUser, ApiWorkspace, SessionResponse } from '@tomo/core/api';
import { Store, useStore } from '../../lib/store.ts';
import { setAccountIdentity } from '../identity.ts';
import { setCloudAttacher } from '../projects.ts';
import { api, ApiRequestError } from './api.ts';
import { attachCloud } from './sync.ts';

/**
 * Account state. `unavailable` means no Tomo Cloud server answers (e.g. the app is served as a
 * static site) — everything keeps working locally, account features are simply hidden.
 */
export type CloudState =
  | { status: 'unknown' }
  | { status: 'unavailable' }
  | { status: 'signed-out'; billing: ApiBillingConfig | null; sendsEmail: boolean }
  | { status: 'signed-in'; user: ApiUser; workspaces: ApiWorkspace[]; billing: ApiBillingConfig | null; sendsEmail: boolean };

export const cloudStore = new Store<CloudState>({ status: 'unknown' });
export const useCloud = () => useStore(cloudStore);

/** True when the server emails invites, password resets and verification links. */
export const sendsEmail = (state: CloudState) => (state.status === 'signed-in' || state.status === 'signed-out') && state.sendsEmail;

let billing: ApiBillingConfig | null = null;
let emailing = false;

function signedIn(user: ApiUser, workspaces: ApiWorkspace[]) {
  cloudStore.set({ status: 'signed-in', user, workspaces, billing, sendsEmail: emailing });
  setAccountIdentity({ id: user.id, name: user.name, color: user.color, email: user.email });
}

function signedOut() {
  cloudStore.set({ status: 'signed-out', billing, sendsEmail: emailing });
  setAccountIdentity(null);
}

function applySession(session: SessionResponse) {
  emailing = session.sendsEmail === true;
  if (session.user) signedIn(session.user, session.workspaces);
  else signedOut();
}

/**
 * Everything cloud connections depend on: who is signed in and their plan + role per workspace.
 * Open cloud projects reconnect whenever this changes (sign in/out, upgrade, role change).
 */
function authKey(): string {
  const s = cloudStore.get();
  if (s.status !== 'signed-in') return s.status;
  return `${s.user.id}:${s.workspaces
    .map((w) => `${w.id}/${w.plan}/${w.role}`)
    .sort()
    .join(',')}`;
}
const reattach = () => setCloudAttacher(attachCloud, authKey());

// When no server answered at startup, keep checking (with backoff) so sync resumes by itself.
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retryDelay = 5_000;
function scheduleRetry() {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(() => void initCloud(), retryDelay);
  retryDelay = Math.min(retryDelay * 2, 5 * 60_000);
}
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    if (cloudStore.get().status === 'unavailable') void initCloud();
  });
}

export async function initCloud() {
  if (cloudStore.get().status === 'unknown') reattach();
  try {
    billing = await api.billing.config().catch(() => null);
    applySession(await api.session());
    retryDelay = 5_000;
  } catch (e) {
    if (e instanceof ApiRequestError && e.status === 401) signedOut();
    else {
      cloudStore.set({ status: 'unavailable' });
      scheduleRetry();
    }
  }
  reattach();
}

export async function refreshMe() {
  try {
    applySession(await api.session());
  } catch (e) {
    if (e instanceof ApiRequestError && e.status === 401) signedOut();
  }
  reattach();
}

export async function signIn(email: string, password: string) {
  await api.login(email, password);
  await refreshMe();
}

export async function signUp(email: string, password: string, name: string) {
  await api.signup(email, password, name);
  await refreshMe();
}

/** Sets a new password from an emailed link, which also signs this device in. */
export async function resetPassword(token: string, password: string) {
  await api.resetPassword({ token, password });
  await refreshMe();
}

export async function signOut() {
  await api.logout().catch(() => undefined);
  signedOut();
  reattach();
}

export function workspaceById(id: string | null | undefined): ApiWorkspace | undefined {
  const s = cloudStore.get();
  return s.status === 'signed-in' ? s.workspaces.find((w) => w.id === id) : undefined;
}
